import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import { useNotificationHub } from '../../hooks/useNotificationHub';
import {
  UserNotification,
  getUnreadNotificationCount,
  listUserNotifications,
  markAllNotificationsAsRead,
  markNotificationAsRead,
} from '../../services/userNotificationsApi';
import { NotificationReceivedPayload } from '../../services/notificationHub';

function mapRealtimeNotification(payload: NotificationReceivedPayload): UserNotification {
  return {
    id: payload.id,
    title: payload.title,
    message: payload.message,
    type: payload.type,
    read: false,
    createdAtUtc: payload.timestamp,
    actionUrl: payload.actionUrl ?? null,
  };
}

export function NotificationDropdown() {
  const { isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<UserNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleRealtimeNotification = useCallback((payload: NotificationReceivedPayload) => {
    const notification = mapRealtimeNotification(payload);

    setUnreadCount((current) => current + 1);
    setItems((current) => {
      if (current.some((item) => item.id === notification.id)) {
        return current;
      }

      return [notification, ...current].slice(0, 15);
    });

    toast.info(notification.title, {
      autoClose: 5000,
    });
  }, []);

  useNotificationHub({
    enabled: isAuthenticated,
    onNotification: handleRealtimeNotification,
  });

  async function refreshBadge(): Promise<void> {
    try {
      setUnreadCount(await getUnreadNotificationCount());
    } catch {
      setUnreadCount(0);
    }
  }

  async function loadNotifications(): Promise<void> {
    setLoading(true);
    try {
      const result = await listUserNotifications(1, 15);
      setItems(result.items);
      setUnreadCount(result.unreadCount);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refreshBadge();
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent): void {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  async function handleToggle(): Promise<void> {
    const next = !open;
    setOpen(next);
    if (next) {
      await loadNotifications();
    }
  }

  async function handleMarkRead(notification: UserNotification): Promise<void> {
    if (notification.read) {
      return;
    }

    try {
      await markNotificationAsRead(notification.id);
      setItems((current) =>
        current.map((item) => (item.id === notification.id ? { ...item, read: true } : item)),
      );
      setUnreadCount((current) => Math.max(0, current - 1));
    } catch {
      // ignore
    }
  }

  async function handleMarkAllRead(): Promise<void> {
    try {
      await markAllNotificationsAsRead();
      setItems((current) => current.map((item) => ({ ...item, read: true })));
      setUnreadCount(0);
    } catch {
      // ignore
    }
  }

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        className="relative rounded-xl p-2 text-primary-700 hover:bg-primary-500/10"
        title="Notificações"
        aria-label="Notificações"
        onClick={() => void handleToggle()}
      >
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0"
          />
        </svg>
        {unreadCount > 0 ? (
          <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        ) : null}
      </button>

      {open ? (
        <div className="absolute right-0 z-30 mt-2 w-96 max-w-[calc(100vw-2rem)] rounded-2xl border border-secondary-200 bg-white text-secondary-800 shadow-lg">
          <div className="flex items-center justify-between border-b border-secondary-100 px-4 py-3">
            <h3 className="text-sm font-semibold">Notificações</h3>
            {unreadCount > 0 ? (
              <button
                type="button"
                className="text-xs font-medium text-primary-600 hover:underline"
                onClick={() => void handleMarkAllRead()}
              >
                Marcar todas como lidas
              </button>
            ) : null}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <p className="px-4 py-6 text-sm text-secondary-500">Carregando...</p>
            ) : items.length === 0 ? (
              <p className="px-4 py-6 text-sm text-secondary-500">Nenhuma notificação.</p>
            ) : (
              items.map((notification) => (
                <div
                  key={notification.id}
                  className={`border-b border-secondary-50 px-4 py-3 ${notification.read ? 'bg-white' : 'bg-primary-50/40'}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-secondary-900">{notification.title}</p>
                      <p className="mt-1 text-sm text-secondary-600">{notification.message}</p>
                      <p className="mt-1 text-xs text-secondary-400">
                        {new Date(notification.createdAtUtc).toLocaleString('pt-BR')}
                      </p>
                    </div>
                    {!notification.read ? (
                      <button
                        type="button"
                        className="shrink-0 text-xs text-primary-600 hover:underline"
                        onClick={() => void handleMarkRead(notification)}
                      >
                        Lida
                      </button>
                    ) : null}
                  </div>
                  {notification.actionUrl ? (
                    <Link
                      to={notification.actionUrl}
                      className="mt-2 inline-block text-xs font-medium text-primary-600 hover:underline"
                      onClick={() => {
                        setOpen(false);
                        void handleMarkRead(notification);
                      }}
                    >
                      Abrir
                    </Link>
                  ) : null}
                </div>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
